import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { voucherService } from "../../../../services/vouchers/voucherService";
import { agentService } from "../../../../services/agent/agentService";
import { getAssetUrl, getProxyUrl } from "../../../../utils/assetUtils";
import MainLayout from "../../../../Layout/MainLayout";
import PageAnimator from "../../../../components/layout/PageAnimator";
import PingModal from "../../../../components/modals/PingModal";
import PDFVoucherView from "../Shared/PDFVoucherView";
import VoucherDashboardSkeleton from "../VoucherList/VoucherDashboardSkeleton";

// Subsections
import VoucherHeroSection from "./sections/HeroSection/VoucherHeroSection";
import TourBookingsSection from "./sections/TourBookings/TourBookingsSection";
import OnTripServicesSection from "./sections/OnTripServices/OnTripServicesSection";
import InAppBookingsSection from "./sections/InAppBookings/InAppBookingsSection";
import { getInviteMessage } from "./utils/dashboardHelpers";

const VoucherDashboardPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [voucher, setVoucher] = useState(null);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setNotificationType] = useState("welcome");
  const [reservations, setReservations] = useState([]);

  const [isDownloading, setIsDownloading] = useState(false);

  // Profile details and PDF generation ref
  const [logo, setLogo] = useState("");
  const [agentName, setAgentName] = useState("");
  const [agentEmail, setAgentEmail] = useState("");
  const [agentPhone, setAgentPhone] = useState("");
  const [agentAddress, setAgentAddress] = useState("");
  const [city, setCity] = useState("");
  const [pincode, setPincode] = useState("");
  const pdfRef = useRef(null);

  const [pingModal, setPingModal] = useState({
    isOpen: false,
    bookingId: null,
    guestName: null
  });

  const fetchVoucherData = async () => {
    try {
      setLoading(true);
      const voucherRes = await voucherService.getVoucherById(id);
      setVoucher(voucherRes?.voucher || voucherRes);

      try {
        const guestsRes = await voucherService.getVoucherGuests(id);
        setGuests(guestsRes?.guests || guestsRes || []);
      } catch (gErr) {
        console.warn("Failed to fetch guests:", gErr.message);
      }

      try {
        const reservationsRes = await voucherService.getVoucherReservations(id);
        setReservations(reservationsRes?.reservations || reservationsRes || []);
      } catch (rErr) {
        console.warn("Failed to fetch guest reservations:", rErr.message);
      }

      const profileResponse = await agentService.getProfile();
      const profileData = profileResponse?.data?.agent || {};
      setAgentAddress(profileData.address || '');
      setCity(profileData.city || '');
      setPincode(profileData.pincode || '');
      setAgentName(profileData.company_name || '');
      setAgentEmail(profileData.email || '');
      setAgentPhone(profileData.phone_number || '');

      if (profileData.logo) {
        try {
          const proxyUrl = getProxyUrl(profileData.logo);
          const res = await fetch(proxyUrl);
          const blob = await res.blob();
          const reader = new FileReader();
          reader.onloadend = () => setLogo(reader.result);
          reader.readAsDataURL(blob);
        } catch (err) {
          const finalLogo = getAssetUrl(profileData.logo);
          setLogo(finalLogo);
        }
      }
    } catch (err) {
      console.error("Failed to load voucher details:", err);
      toast.error("Failed to load voucher details.");
      navigate("/voucher");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVoucherData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const generateVoucherPdf = async (targetVoucher = null) => {
    const container = pdfRef.current;
    if (!container) throw new Error("PDF container element not ready");

    await new Promise((resolve) => setTimeout(resolve, 150));

    const MARGIN = 28;
    const fullCanvas = await html2canvas(container, {
      scale: 2.5,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
    });

    const pdf = new jsPDF("p", "pt", "a4");
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const contentW = pageW - MARGIN * 2;
    const contentH = pageH - MARGIN * 2;

    const canvasToPagePt = contentW / fullCanvas.width;
    const pageHeightPx = contentH / canvasToPagePt;
    const containerTop = container.getBoundingClientRect().top;

    const lastPageEl = container.querySelector('[data-pdf-section="last-page"]');
    const lastPageTop = lastPageEl
      ? Math.round((lastPageEl.getBoundingClientRect().top - containerTop) * 2.5)
      : fullCanvas.height;

    const mainContentEnd = lastPageEl ? lastPageTop : fullCanvas.height;

    const sections = container.querySelectorAll('[data-pdf-section]');
    const sectionBreaks = [0];
    sections.forEach(sec => {
      if (sec === lastPageEl) return;
      const relTop = Math.round((sec.getBoundingClientRect().top - containerTop) * 2.5);
      if (relTop > 0 && relTop < mainContentEnd) sectionBreaks.push(relTop);
    });
    sectionBreaks.push(mainContentEnd);

    const pageSlices = [];
    let pageStart = 0;

    while (pageStart < mainContentEnd) {
      const idealEnd = pageStart + pageHeightPx;

      if (idealEnd >= mainContentEnd) {
        pageSlices.push({ start: pageStart, end: mainContentEnd });
        break;
      }

      let bestBreak = idealEnd;
      for (let i = sectionBreaks.length - 1; i >= 0; i--) {
        if (sectionBreaks[i] <= idealEnd && sectionBreaks[i] > pageStart && sectionBreaks[i] < mainContentEnd) {
          bestBreak = sectionBreaks[i];
          break;
        }
      }

      pageSlices.push({ start: pageStart, end: bestBreak });
      pageStart = bestBreak;
    }

    if (lastPageEl && lastPageTop < fullCanvas.height) {
      pageSlices.push({ start: lastPageTop, end: fullCanvas.height, isLastPage: true });
    }

    pageSlices.forEach((slice, idx) => {
      if (idx > 0) pdf.addPage();

      const sliceH = slice.end - slice.start;
      const sliceCanvas = document.createElement('canvas');
      sliceCanvas.width = fullCanvas.width;
      sliceCanvas.height = sliceH;
      const ctx = sliceCanvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
      ctx.drawImage(fullCanvas, 0, -slice.start);

      let sliceHPt = sliceH * canvasToPagePt;
      let drawW = contentW;
      let drawH = sliceHPt;
      let offsetX = MARGIN;
      let offsetY = MARGIN;

      if (slice.isLastPage || (lastPageEl && idx === pageSlices.length - 1)) {
        if (sliceHPt > contentH) {
          const scale = contentH / sliceHPt;
          drawW = contentW * scale;
          drawH = contentH;
          offsetX = MARGIN + (contentW - drawW) / 2;
        }
      }

      pdf.addImage(sliceCanvas.toDataURL('image/jpeg', 1.0), 'JPEG', offsetX, offsetY, drawW, drawH);

      if (slice.isLastPage || (lastPageEl && idx === pageSlices.length - 1)) {
        const voucherData = targetVoucher || voucher;
        const currentBookingId = container.getAttribute('data-booking-id') || voucherData?.booking_id || voucherData?.bookingID || 'a1b2c';
        const playStoreUrl = `https://play.google.com/store/apps/details?id=live.trrip.app&hl=en_IN&bookingId=${currentBookingId}`;
        const appStoreUrl = `https://apps.apple.com/us/app/trrip-your-ontour-assistant/id6759519854`;

        pdf.link(
          offsetX + drawW * 0.052,
          offsetY + drawH * 0.745,
          drawW * 0.220,
          drawH * 0.055,
          { url: playStoreUrl }
        );

        pdf.link(
          offsetX + drawW * 0.052,
          offsetY + drawH * 0.805,
          drawW * 0.220,
          drawH * 0.055,
          { url: appStoreUrl }
        );
      }
    });

    const activeVoucher = targetVoucher || voucher;
    const fileName = `${activeVoucher?.guest_information?.guest_name || 'Guest'}_Voucher.pdf`;
    const pdfBlob = pdf.output('blob');

    return { pdf, pdfBlob, fileName };
  };

  const saveContactsDirectly = async (updatedContacts) => {
    const updatedVoucher = {
      ...voucher,
      contact_details: updatedContacts
    };

    setVoucher(updatedVoucher);
    const toastId = toast.loading("Saving contacts & updating S3 PDF...");

    setTimeout(async () => {
      try {
        const { pdfBlob, fileName } = await generateVoucherPdf(updatedVoucher);

        const sanitizedVoucher = JSON.parse(JSON.stringify(updatedVoucher));
        if (sanitizedVoucher.hotel_bookings) {
          sanitizedVoucher.hotel_bookings = sanitizedVoucher.hotel_bookings.map(({ id, ...rest }) => ({
            ...rest,
            location_id: rest.location_id === "" ? null : rest.location_id
          }));
        }
        if (sanitizedVoucher.tour_info && sanitizedVoucher.tour_info.destinations) {
          sanitizedVoucher.tour_info.destinations = sanitizedVoucher.tour_info.destinations.map(dest => ({
            ...dest,
            location_id: dest.location_id === "" ? null : dest.location_id
          }));
        }
        if (sanitizedVoucher.travel_bookings) {
          sanitizedVoucher.travel_bookings = sanitizedVoucher.travel_bookings.map(({ id, ...rest }) => rest);
        }
        if (sanitizedVoucher.itinerary) {
          sanitizedVoucher.itinerary = sanitizedVoucher.itinerary.map(({ id, ...rest }) => ({
            ...rest,
            remarks: rest.remarks || [],
            timings: rest.timings || []
          }));
        }
        if (sanitizedVoucher.contact_details) {
          sanitizedVoucher.contact_details = sanitizedVoucher.contact_details.map(({ id, ...rest }) => rest);
        }

        const formData = new FormData();
        formData.append('pdfFile', pdfBlob, fileName);
        formData.append('voucherData', JSON.stringify({
          booking_id: voucher.booking_id,
          ...sanitizedVoucher,
          itenary_link: fileName
        }));

        await voucherService.updateVoucher(voucher._id, formData, true);
        toast.dismiss(toastId);
        toast.success('Contacts updated successfully!');
        fetchVoucherData();
      } catch (error) {
        toast.dismiss(toastId);
        console.error("Save error:", error);
        toast.error('Failed to save contacts.');
      }
    }, 100);
  };

  const handleUpdateVoucher = async (updatedVoucher) => {
    setVoucher(updatedVoucher);
    try {
      const sanitizedVoucher = JSON.parse(JSON.stringify(updatedVoucher));
      if (sanitizedVoucher.hotel_bookings) {
        sanitizedVoucher.hotel_bookings = sanitizedVoucher.hotel_bookings.map(({ id, ...rest }) => ({
          ...rest,
          location_id: rest.location_id === "" ? null : rest.location_id
        }));
      }
      if (sanitizedVoucher.travel_bookings) {
        sanitizedVoucher.travel_bookings = sanitizedVoucher.travel_bookings.map(({ id, ...rest }) => rest);
      }

      const formData = new FormData();
      formData.append('voucherData', JSON.stringify({
        booking_id: voucher.booking_id,
        ...sanitizedVoucher
      }));

      if (pdfRef.current) {
        try {
          const { pdfBlob, fileName } = await generateVoucherPdf(sanitizedVoucher);
          formData.append('pdfFile', pdfBlob, fileName);
        } catch (pdfErr) {
          console.warn("Background PDF update warning:", pdfErr);
        }
      }

      const res = await voucherService.updateVoucher(voucher._id, formData, true);
      if (res?.voucher) {
        setVoucher(res.voucher);
      }
      return res;
    } catch (err) {
      console.error("Failed to update voucher:", err);
      toast.error('Failed to update voucher.');
      throw err;
    }
  };

  const handleDownload = async () => {
    if (!voucher) return;
    setIsDownloading(true);
    const toastId = toast.loading("Downloading voucher PDF...");
    try {
      if (pdfRef.current) {
        const { pdf, pdfBlob, fileName } = await generateVoucherPdf(voucher);
        pdf.save(fileName);
        toast.dismiss(toastId);
        toast.success("Voucher PDF downloaded!");

        try {
          const updateFormData = new FormData();
          updateFormData.append("voucherData", JSON.stringify(voucher));
          updateFormData.append("pdfFile", pdfBlob, fileName);
          voucherService.updateVoucher(voucher._id, updateFormData, true).then(res => {
            if (res?.voucher) {
              setVoucher(res.voucher);
            }
          }).catch(e => console.warn("Background PDF update to S3 failed:", e));
        } catch (syncErr) {
          console.warn("Background sync error:", syncErr);
        }
        return;
      }

      const response = await voucherService.downloadVoucherPdf(voucher._id, 'download');
      const blob = new Blob([response], { type: 'application/pdf' });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${voucher.guest_information?.guest_name || 'Guest'}_Voucher.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      toast.dismiss(toastId);
      toast.success("Voucher PDF downloaded!");
    } catch (error) {
      toast.dismiss(toastId);
      console.error("Download failed:", error);
      if (voucher?.itenary_link) {
        const finalUrl = getAssetUrl(voucher.itenary_link);
        const link = document.createElement('a');
        link.href = finalUrl;
        link.download = `${voucher.guest_information?.guest_name || 'Guest'}_Voucher.pdf`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }
      toast.error("Download failed.");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCopyInviteMessage = () => {
    localStorage.setItem('has_shared_app', 'true');
    localStorage.setItem('trrip_step_share_completed', 'true');
    agentService.markAppShared();
    const msg = getInviteMessage(voucher);
    navigator.clipboard.writeText(msg).then(() => {
      toast.success("Invite message copied!");
    });
  };

  const handleWhatsAppShare = () => {
    localStorage.setItem('has_shared_app', 'true');
    localStorage.setItem('trrip_step_share_completed', 'true');
    agentService.markAppShared();
    const url = `https://api.whatsapp.com/send?text=${encodeURIComponent(getInviteMessage(voucher))}`;
    window.open(url, '_blank');
  };

  const handlePingUser = (guestName = null) => {
    const hasTravellerLoggedIn =
      (Array.isArray(guests) && guests.length > 0) ||
      (Array.isArray(voucher?.active_guests) && voucher.active_guests.length > 0);

    if (!hasTravellerLoggedIn) {
      toast.error("No traveller logged in - ask traveller to download app");
      return;
    }

    setPingModal({
      isOpen: true,
      bookingId: voucher?.booking_id || voucher?.bookingID,
      guestName: guestName
    });
  };

  const executePingUser = async (bookingId, message, guestName = null) => {
    try {
      setLoading(true);
      await voucherService.pingTraveller({
        booking_id: bookingId,
        message: message,
        guest_name: guestName
      });
      toast.success(guestName ? `Push notification sent to ${guestName}!` : "Push notification sent to all guests!");
      fetchVoucherData();
    } catch (err) {
      console.error("Failed to ping user", err);
      const errMsg = err.response?.data?.message;
      if (errMsg && (errMsg.includes('registered devices') || errMsg.includes('No traveller') || errMsg.includes('no registered devices'))) {
        toast.error("No traveller logged in - ask traveller to download app");
      } else {
        toast.error(errMsg || "No traveller logged in - ask traveller to download app");
      }
    } finally {
      setLoading(false);
      setPingModal(prev => ({ ...prev, isOpen: false }));
    }
  };

  if (loading) {
    return (
      <MainLayout activeTab="voucher">
        <VoucherDashboardSkeleton />
      </MainLayout>
    );
  }

  return (
    <MainLayout activeTab="voucher">
      <PageAnimator className="bg-slate-50 min-h-screen text-[#3a4d5e] pb-16 font-sans">
        {/* Custom Styles */}
        <style>{`
          .vc-wrap {
            width: 100%;
            max-width: 1320px;
            margin: 0 auto;
            padding: clamp(14px, 2.5vw, 36px) clamp(10px, 2vw, 28px) clamp(40px, 6vw, 72px);
            box-sizing: border-box;
            overflow-x: clip;
          }
          .vc-mono { font-family: 'JetBrains Mono', monospace; }

          /* Page Header */
          .vc-page-head {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            margin-bottom: clamp(16px, 2.5vw, 26px);
            flex-wrap: wrap;
            width: 100%;
          }
          .vc-page-head h1 {
            font-size: clamp(19px, 2.2vw, 26px);
            font-weight: 700;
            color: #1f2937;
            letter-spacing: -.02em;
            line-height: 1.2;
          }
          .vc-page-head p {
            font-size: clamp(11.5px, 1.2vw, 13.5px);
            color: #6b7280;
            margin-top: 3px;
            font-weight: 500;
          }
          .vc-head-actions {
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
            align-items: center;
          }
          .vc-btn {
            font-family: inherit;
            font-size: clamp(11.5px, 1.1vw, 13px);
            font-weight: 600;
            border-radius: 999px;
            padding: 8px 14px;
            border: 1px solid #e7ecea;
            background: #ffffff;
            color: #374151;
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
            transition: all .15s ease;
          }
          .vc-btn:hover { border-color: #067978; color: #067978; }
          .vc-btn-primary { background: #067978; border-color: #067978; color: #fff; }
          .vc-btn-primary:hover { background: #045d5c; border-color: #045d5c; color: #fff; }

          /* Hero Card */
          .vc-hero {
            background: linear-gradient(135deg, #045d5c, #067978 72%);
            color: #fff;
            border-radius: clamp(16px, 2vw, 24px);
            padding: clamp(14px, 2.5vw, 26px);
            box-shadow: 0 4px 6px -1px rgba(16,38,58,.06), 0 20px 40px rgba(6,121,120,.12);
            display: flex;
            justify-content: space-between;
            gap: clamp(14px, 2.2vw, 26px);
            flex-wrap: wrap;
            box-sizing: border-box;
            width: 100%;
            position: relative;
            overflow: visible;
          }
          .vc-hero-main { flex: 1; min-width: 0; width: 100%; }
          .vc-hero-who { display: flex; align-items: center; gap: clamp(10px, 1.8vw, 16px); margin-bottom: clamp(12px, 1.8vw, 18px); }
          .vc-avatar {
            width: clamp(38px, 4.5vw, 50px);
            height: clamp(38px, 4.5vw, 50px);
            border-radius: clamp(10px, 1.4vw, 15px);
            background: rgba(255,255,255,.18);
            display: grid;
            place-items: center;
            font-weight: 700;
            font-size: clamp(14px, 1.8vw, 19px);
            flex-shrink: 0;
          }
          .vc-hero-who h2 {
            font-size: clamp(17px, 2vw, 23px);
            font-weight: 800;
            letter-spacing: -.02em;
            line-height: 1.2;
          }
          .vc-hero-who .vc-pax {
            font-size: clamp(11px, 1.1vw, 12.5px);
            color: rgba(255,255,255,.78);
            margin-top: 3px;
            display: flex;
            align-items: center;
            gap: 6px;
          }
          .vc-fact .vc-lab {
            font-size: clamp(10px, 1vw, 11.5px);
            font-weight: 600;
            color: rgba(255,255,255,.70);
            margin-bottom: 2px;
            text-transform: uppercase;
            letter-spacing: .04em;
          }
          .vc-fact .vc-val {
            font-size: clamp(12.5px, 1.2vw, 14.5px);
            font-weight: 700;
            color: #fff;
            word-break: break-word;
          }

          .vc-countdown {
            align-self: center;
            text-align: center;
            background: rgba(255,255,255,.12);
            border: 1px solid rgba(255,255,255,.16);
            border-radius: clamp(12px, 1.6vw, 18px);
            padding: clamp(10px, 1.5vw, 16px) clamp(14px, 2vw, 22px);
            min-width: 0;
            box-sizing: border-box;
          }
          .vc-countdown .vc-num {
            font-size: clamp(28px, 3.5vw, 42px);
            font-weight: 800;
            line-height: 1;
            letter-spacing: -.03em;
          }
          .vc-countdown .vc-unit {
            font-size: clamp(10px, 1vw, 11.5px);
            font-weight: 700;
            letter-spacing: .06em;
            text-transform: uppercase;
            color: rgba(255,255,255,.75);
            margin-top: 3px;
          }
          .vc-countdown .vc-prog {
            margin-top: 6px;
            font-size: clamp(10px, 1vw, 11px);
            color: #9fe7c0;
            font-weight: 700;
          }

          /* 640px Breakpoint controls for Hero Section */
          @media (min-width: 640px) {
            .vc-hero-desktop-right { display: flex !important; }
            .vc-hero-desktop-attached { display: block !important; }
            .vc-hero-mobile-3boxes { display: none !important; }
          }
          @media (max-width: 640px) {
            .vc-hero-desktop-right { display: none !important; }
            .vc-hero-desktop-attached { display: none !important; }
            .vc-hero-mobile-3boxes { display: block !important; }
          }

          /* Section zones */
          .vc-zone { margin-top: clamp(20px, 3vw, 32px); width: 100%; }
          .vc-zone-label { display: flex; align-items: center; gap: 8px; margin: 0 2px 12px; flex-wrap: wrap; }
          .vc-zone-label .vc-tag { font-size: clamp(12.5px, 1.2vw, 14px); font-weight: 700; color: #067978; }
          .vc-zone-label .vc-sub { font-size: clamp(11px, 1vw, 12px); color: #8a9aa5; font-weight: 500; }
          .vc-zone-label .vc-rule { flex: 1; min-width: 30px; height: 1px; background: #e7ecea; }

          .vc-grid { display: grid; gap: clamp(12px, 1.5vw, 18px); width: 100%; min-width: 0; }
          .vc-g-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }

          /* Cards */
          .vc-card {
            display: flex;
            flex-direction: column;
            background: #ffffff;
            border: 1px solid #e7ecea;
            border-radius: clamp(14px, 1.8vw, 18px);
            padding: clamp(12px, 1.6vw, 18px);
            box-shadow: 0 1px 3px rgba(16,38,58,.04), 0 6px 20px rgba(16,38,58,.04);
            min-width: 0;
            width: 100%;
            overflow: hidden;
            box-sizing: border-box;
          }
          .vc-card h3,
          .vc-card h3 span,
          .vc-card-head h3,
          .vc-card-head h3 span {
            font-size: clamp(11px, 1.1vw, 12px) !important;
            font-weight: 600 !important;
            letter-spacing: .05em;
            text-transform: uppercase;
            color: #64748b !important;
          }
          .vc-card h3 {
            display: flex;
            align-items: center;
            gap: 8px;
          }
          .vc-ic {
            width: clamp(28px, 3vw, 32px);
            height: clamp(28px, 3vw, 32px);
            border-radius: 8px;
            background: #e6f3ec;
            display: grid;
            place-items: center;
            font-size: clamp(13px, 1.4vw, 15px);
            flex-shrink: 0;
          }
          .vc-card-head {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 8px;
            margin-bottom: 12px;
            flex-wrap: wrap;
          }
          .vc-badge {
            font-size: 10px !important;
            font-weight: 700 !important;
            letter-spacing: .04em;
            padding: 3px 8px;
            border-radius: 999px;
            white-space: nowrap;
            text-transform: uppercase;
          }
          .vc-b-green { background: #d4f0e0; color: #0a5235; }
          .vc-b-blue { background: #e7f0fd; color: #2f6fd6; }
          .vc-b-amber { background: #fdf2dd; color: #c9810b; }

          .vc-expandable { cursor: pointer; transition: all .15s ease; }
          .vc-expandable:hover { border-color: #cfe6da; box-shadow: 0 2px 4px rgba(16,38,58,.05), 0 16px 36px rgba(10,82,53,.08); }
          .vc-line {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 10px;
            padding: 8px 0;
            border-top: 1px solid #f1f5f9;
            min-width: 0;
          }
          .vc-line:first-of-type { border-top: none; }
          .vc-line .vc-nm {
            font-size: clamp(12.5px, 1.2vw, 13.5px);
            font-weight: 500;
            color: #475569;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .vc-line .vc-loc {
            font-size: clamp(10.5px, 1.1vw, 11.5px);
            color: #94a3b8;
            margin-top: 1px;
            font-weight: 400;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .vc-line .vc-dt {
            font-size: clamp(11.5px, 1.1vw, 12.5px);
            font-weight: 400;
            color: #64748b;
            white-space: nowrap;
            text-align: right;
            flex-shrink: 0;
          }
          .vc-expand-cue {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
            margin-top: auto;
            padding-top: 12px;
            font-size: clamp(12px, 1.1vw, 13px);
            font-weight: 500;
            color: #067978;
            cursor: pointer;
            transition: .15s;
          }
          .vc-expand-cue:hover { opacity: 0.8; }

          .vc-today-list { display: flex; flex-direction: column; gap: 8px; margin-top: 2px; }
          .vc-ti { display: flex; gap: 10px; align-items: flex-start; min-width: 0; }
          .vc-ti .vc-time {
            font-size: 11.5px;
            font-weight: 400;
            color: #64748b;
            min-width: 48px;
            padding-top: 1px;
            font-family: inherit;
            flex-shrink: 0;
          }
          .vc-ti .vc-what {
            font-size: clamp(12px, 1.2vw, 13px);
            color: #475569;
            font-weight: 400;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .vc-ti .vc-where {
            font-size: 11px;
            color: #94a3b8;
            font-weight: 400;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .vc-comm {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 10px;
            padding: 10px 12px;
            border: 1px solid #e7ecea;
            border-radius: 11px;
            font-size: clamp(12px, 1.2vw, 13px);
            font-weight: 500;
            color: #475569;
            cursor: pointer;
            background: #fff;
            transition: .15s;
            margin-top: 6px;
          }
          .vc-comm:first-of-type { margin-top: 2px; }
          .vc-comm:hover { border-color: #cfe6da; background: #fbfdfc; }
          .vc-comm.vc-lead { border-color: #cfe6da; background: #e6f3ec; }
          .vc-comm .vc-arr { color: #067978; font-weight: 700; }

          .vc-contact { display: flex; align-items: center; gap: 10px; padding: 8px 0; border-top: 1px solid #f1f5f9; min-width: 0; }
          .vc-contact:first-of-type { border-top: none; padding-top: 2px; }
          .vc-c-ic {
            width: 32px;
            height: 32px;
            border-radius: 9px;
            background: #e6f3ec;
            display: grid;
            place-items: center;
            font-size: 14px;
            flex-shrink: 0;
          }
          .vc-contact .vc-cn {
            font-size: clamp(12.5px, 1.2vw, 13.5px);
            font-weight: 600;
            color: #374151;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .vc-contact .vc-cr {
            font-size: 11px;
            color: #8a9aa5;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          .vc-contact .vc-call { margin-left: auto; font-size: 11.5px; font-weight: 700; color: #067978; text-decoration: none; flex-shrink: 0; }

          .vc-activity {
            border-color: #cfe6da;
            box-shadow: 0 2px 4px rgba(16,38,58,.05), 0 18px 40px rgba(6,121,120,.08);
            background: rgba(255, 255, 255, 0.75);
            backdrop-filter: blur(12px);
            -webkit-backdrop-filter: blur(12px);
          }
          .vc-act-head { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 4px; flex-wrap: wrap; }
          .vc-act-head .vc-lead-q { font-size: clamp(11px, 1.1vw, 12px) !important; font-weight: 600 !important; letter-spacing: .05em; text-transform: uppercase; color: #64748b !important; }
          .vc-card-head .vc-count, .vc-act-head .vc-count { font-size: 11px; font-weight: 500; color: #067978; background: #e6f3ec; padding: 3px 9px; border-radius: 999px; white-space: nowrap; }
          .vc-act-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 12px; margin-top: 12px; }
          .vc-act { border: 1px solid rgba(255, 255, 255, 0.9); border-radius: 12px; padding: 12px 14px; background: rgba(255, 255, 255, 0.65); box-shadow: 0 2px 8px rgba(16,38,58,0.02); transition: .15s; min-width: 0; }
          .vc-act .vc-cat { font-size: 10px; font-weight: 500; letter-spacing: .04em; text-transform: uppercase; color: #8a9aa5; margin-bottom: 4px; }
          .vc-act .vc-name { font-size: clamp(12.5px, 1.2vw, 13.5px); font-weight: 400; color: #475569; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

          /* Modals */
          .vc-overlay {
            position: fixed;
            left: 0;
            right: 0;
            bottom: 0;
            top: var(--portal-header-height, 64px);
            background: rgba(15, 23, 42, 0.60);
            backdrop-filter: blur(5px);
            -webkit-backdrop-filter: blur(5px);
            display: none;
            align-items: center;
            justify-content: center;
            padding: clamp(8px, 2vw, 20px);
            z-index: 50;
            box-sizing: border-box;
          }
          .vc-overlay.vc-open { display: flex; }
          .vc-modal {
            background: #fff;
            border-radius: clamp(16px, 2vw, 24px);
            max-width: 600px;
            width: 100%;
            max-height: min(calc(100vh - var(--portal-header-height, 64px) - 2rem), 840px);
            display: flex;
            flex-direction: column;
            overflow: hidden;
            box-shadow: 0 25px 60px -15px rgba(0,0,0,.35);
            animation: pop .18s ease;
            margin: auto;
            box-sizing: border-box;
          }
          .vc-modal.vc-modal-overflow-visible,
          .vc-modal.vc-modal-overflow-visible .vc-m-body {
            overflow: visible !important;
            max-height: none !important;
          }
          @keyframes pop { from { transform: translateY(8px) scale(.99); opacity: 0; } to { transform: none; opacity: 1; } }
          .vc-m-head {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            padding: clamp(14px, 2vw, 18px) clamp(16px, 2.5vw, 24px);
            border-bottom: 1px solid #f1f5f9;
            background: #fff;
            flex-shrink: 0;
          }
          .vc-m-head h4 { font-size: clamp(14px, 1.4vw, 15.5px); font-weight: 600; color: #1e293b; display: flex; align-items: center; gap: 8px; }
          .vc-m-close {
            width: 32px;
            height: 32px;
            border-radius: 10px;
            border: 1px solid #e2e8f0;
            background: #f8fafc;
            cursor: pointer;
            font-size: 13px;
            color: #64748b;
            display: grid;
            place-items: center;
            transition: all .15s;
            font-weight: bold;
            flex-shrink: 0;
          }
          .vc-m-close:hover { background: #fee2e2; color: #ef4444; border-color: #fecaca; }
          .vc-m-body {
            padding: clamp(14px, 2.2vw, 22px) clamp(14px, 2.5vw, 24px);
            overflow-y: auto;
            flex: 1;
            overscroll-behavior: contain;
          }

          .vc-stay { border: 1px solid #e2e8f0; border-radius: clamp(12px, 1.5vw, 16px); padding: clamp(12px, 2vw, 18px); margin-bottom: 12px; background: #fff; box-shadow: 0 2px 8px rgba(0,0,0,0.02); }
          .vc-stay-top { display: flex; justify-content: space-between; gap: 10px; margin-bottom: 12px; flex-wrap: wrap; }
          .vc-stay-top .vc-sn { font-size: clamp(13.5px, 1.3vw, 15px); font-weight: 500; color: #1e293b; }
          .vc-stay-top .vc-sl { font-size: 11.5px; color: #64748b; font-weight: 400; }
          .vc-stay-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(130px, 1fr)); gap: 10px; }
          .vc-sg .vc-l { font-size: 10px; font-weight: 400; text-transform: uppercase; color: #94a3b8; margin-bottom: 2px; letter-spacing: 0.03em; }
          .vc-sg .vc-v { font-size: clamp(12px, 1.2vw, 13px); font-weight: 500; color: #334155; word-break: break-word; }

          .vc-flt-route { display: flex; align-items: center; justify-content: space-between; gap: clamp(8px, 1.5vw, 14px); margin: 4px 0 14px; }
          .vc-flt-end { text-align: center; min-width: 0; }
          .vc-flt-end .vc-code { font-size: clamp(18px, 2.4vw, 24px); font-weight: 800; color: #1e293b; letter-spacing: -.02em; }
          .vc-flt-end .vc-city { font-size: clamp(10.5px, 1.1vw, 12px); color: #64748b; font-weight: 600; }
          .vc-flt-end .vc-time { font-size: clamp(11px, 1.1vw, 12.5px); font-weight: 700; color: #067978; margin-top: 2px; }
          .vc-flt-path { flex: 1; position: relative; height: 2px; background: #cfe6da; margin: 0 4px; min-width: 40px; }
          .vc-flt-path .vc-plane { position: absolute; top: 50%; left: 18%; transform: translate(-50%,-50%); font-size: clamp(14px, 1.6vw, 18px); }
          .vc-flt-path .vc-done { position: absolute; left: 0; top: 0; height: 100%; width: 18%; background: #067978; }
          .vc-flt-meta { display: grid; grid-template-columns: repeat(auto-fill, minmax(130px, 1fr)); gap: 10px; padding: clamp(10px, 1.5vw, 14px); background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; margin-top: 4px; }
          .vc-flt-meta .vc-l { font-size: 10px; font-weight: 700; text-transform: uppercase; color: #475569; margin-bottom: 2px; letter-spacing: 0.02em; }
          .vc-flt-meta .vc-v { font-size: clamp(12px, 1.2vw, 13px); font-weight: 600; color: #1e293b; word-break: break-word; }

          .vc-day { margin-bottom: 16px; }
          .vc-day-h { font-size: 11.5px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; color: #067978; margin-bottom: 8px; display: flex; align-items: center; gap: 8px; }
          .vc-day-h .vc-chip { background: #e6f3ec; color: #067978; padding: 2px 8px; border-radius: 6px; }
          .vc-it-row { display: flex; gap: 10px; padding: 6px 0; border-left: 2px solid #cfe6da; padding-left: 12px; margin-left: 4px; }
          .vc-it-row .vc-t { font-size: 11px; font-weight: 800; color: #067978; min-width: 48px; font-family: 'JetBrains Mono', monospace; flex-shrink: 0; }
          .vc-it-row .vc-x .vc-w { font-size: clamp(12.5px, 1.2vw, 13.5px); font-weight: 600; color: #374151; }
          .vc-it-row .vc-x .vc-p { font-size: 11px; color: #8a9aa5; }

          /* Responsive Breakpoints from 320px to 1920px */
          @media (max-width: 1080px) {
            .vc-g-3 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          }

          @media (max-width: 720px) {
            .vc-g-3 { grid-template-columns: minmax(0, 1fr); }
            .vc-page-head { flex-direction: column; align-items: stretch; gap: 12px; }
            .vc-head-actions { width: 100%; justify-content: stretch; }
            .vc-head-actions .vc-btn { flex: 1; min-width: 0; }
            .vc-hero { flex-direction: column; }
            .vc-countdown { align-self: flex-start; }
          }

          @media (max-width: 480px) {
            .vc-head-actions .vc-btn-primary { width: 100%; }
          }
        `}</style>

        <div className="vc-wrap">
          {/* Section 1: Hero Section */}
          <VoucherHeroSection
            voucher={voucher}
            guests={guests}
            id={id}
            isDownloading={isDownloading}
            onDownload={handleDownload}
            onViewItinerary={() => navigate(`/voucher-itinerary/${id}`)}
            onCopyInviteMessage={handleCopyInviteMessage}
            onWhatsAppShare={handleWhatsAppShare}
          />

          {/* Section 2: Tour Bookings Section */}
          <TourBookingsSection
            voucher={voucher}
            id={id}
            setVoucher={setVoucher}
            onUpdateVoucher={handleUpdateVoucher}
          />

          {/* Section 3: On-Trip Services Section */}
          <OnTripServicesSection
            voucher={voucher}
            guests={guests}
            handlePingUser={handlePingUser}
            setNotificationType={setNotificationType}
            onSaveContacts={saveContactsDirectly}
          />

          {/* Section 4: In-App Bookings Section */}
          <InAppBookingsSection
            voucher={voucher}
            reservations={reservations}
            onCopyInviteMessage={handleCopyInviteMessage}
            onWhatsAppShare={handleWhatsAppShare}
          />
        </div>

        {/* Hidden PDF Generator Container */}
        <div style={{ position: 'absolute', top: 0, left: '-9999px', width: '794px', background: 'white' }}>
          <div ref={pdfRef}>
            {voucher && (
              <PDFVoucherView
                data={voucher}
                bookingId={voucher?.booking_id || ''}
                logo={logo}
                agentName={agentName}
                agentEmail={agentEmail}
                agentPhone={agentPhone}
                agentAddress={agentAddress}
                city={city}
                pincode={pincode}
                customTheme={
                  voucher?.voucher_theme
                    ? {
                      isCustom: Boolean(voucher.voucher_theme.is_custom || voucher.voucher_theme.isCustom),
                      primaryColor: voucher.voucher_theme.primary_color || voucher.voucher_theme.primaryColor || '#0d9488',
                      secondaryColor: voucher.voucher_theme.secondary_color || voucher.voucher_theme.secondaryColor || '#1d4ed8',
                      textColor: voucher.voucher_theme.text_color || voucher.voucher_theme.textColor || '#111827',
                      headerBgColor: voucher.voucher_theme.header_bg_color || voucher.voucher_theme.headerBgColor || '#ffffff',
                      include_itinerary: Boolean(voucher.voucher_theme.include_itinerary)
                    }
                    : { isCustom: false, primaryColor: '#0d9488', secondaryColor: '#1d4ed8', textColor: '#111827', headerBgColor: '#ffffff', include_itinerary: false }
                }
              />
            )}
          </div>
        </div>

        {/* Ping Modal */}
        <PingModal
          isOpen={pingModal.isOpen}
          onClose={() => setPingModal(prev => ({ ...prev, isOpen: false }))}
          onConfirm={(message) => executePingUser(pingModal.bookingId, message, pingModal.guestName)}
          bookingId={pingModal.bookingId}
          guestName={pingModal.guestName}
        />
      </PageAnimator>
    </MainLayout>
  );
};

export default VoucherDashboardPage;
